import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import ChatbotAi from "../components/ChatbotAi";
import '../css/calculatorpages.css';

const HRACalculator = () => {
    const [metroCity, setMetroCity] = useState(true);
    const [basicSalary, setBasicSalary] = useState('');
    const [dearnessAllowance, setDearnessAllowance] = useState('');
    const [hraReceived, setHraReceived] = useState('');
    const [totalRentPaid, setTotalRentPaid] = useState('');

    const [hraTaxable, setHraTaxable] = useState(null);
    const [hraExempted, setHraExempted] = useState(null);

    const navigate = useNavigate();

    const handleBackClick = () => {
        navigate('/#financial-calculators-section');
    };

    const calculateHRA = () => {
        const B = parseFloat(basicSalary);
        const DA = parseFloat(dearnessAllowance) || 0;
        const HRA = parseFloat(hraReceived);
        const Rent = parseFloat(totalRentPaid);

        if (B >= 0 && HRA >= 0 && Rent >= 0) {
            const actualHRA = HRA;
            const percentageOfSalary = metroCity ? 0.50 * (B + DA) : 0.40 * (B + DA);
            const rentPaidMinus10PercentOfSalary = Rent - (0.10 * (B + DA));
            const exemptedHRA = Math.min(actualHRA, percentageOfSalary, Math.max(0, rentPaidMinus10PercentOfSalary));
            const taxableHRA = actualHRA - exemptedHRA;

            setHraExempted(exemptedHRA.toFixed(2));
            setHraTaxable(taxableHRA.toFixed(2));
        } else {
            setHraTaxable(null);
            setHraExempted(null);
        }
    };

    return (
        <div>
            <Helmet>
                <title>HRA Calculator | Calculate Your House Rent Allowance</title>
                <meta name="description" content="Find out your eligible House Rent Allowance deduction immediately. Use the easy HRA calculator to optimize your tax savings today." />
                <link rel="canonical" href="https://policysamjho.com/calculators/hra-calculator/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "HRA Calculator",
                        "description": "Find out your eligible House Rent Allowance deduction immediately. Use the easy HRA calculator to optimize your tax savings today.",
                        "url": "https://policysamjho.com/calculators/hra-calculator/",
                        "applicationCategory": "FinancialApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "provider": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>

            <div className="calculator-page-container">
                <button onClick={handleBackClick} className="calculator-back-button">
                    Back
                </button>
                <div className="calculator-content-wrapper">
                    <div className="calculator-info-section">
                        <div className="calculator-header-content">
                            <FontAwesomeIcon icon={faBuilding} className="calculator-header-icon" />
                            <h1 className="calculator-title">HRA Calculator</h1>
                        </div>
                        <p className="calculator-description">
                            House Rent Allowance (HRA) is a component in the salary provided by employers to help salaried individuals cover the cost of renting a home. It is partially or fully exempt from income tax under Section 10(13A) of the Income Tax Act. The actual amount of exemption depends on various factors, including your salary, rent paid, and the city in which you reside. HRA Calculator is an easy to use tool that you can use to calculate your HRA exemption instantly.
                        </p>
                    </div>
                    <div className={`calculator-main-section ${hraTaxable === null ? 'no-result' : ''}`}>
                        <div className="calculator-input-section">
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="basicSalary">Basic Salary Received (p.a.)*</label>
                                    <input
                                        type="number"
                                        id="basicSalary"
                                        value={basicSalary}
                                        onChange={(e) => setBasicSalary(e.target.value)}
                                        placeholder="e.g. 500000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="dearnessAllowance">Dearness Allowance received (p.a.)</label>
                                    <input
                                        type="number"
                                        id="dearnessAllowance"
                                        value={dearnessAllowance}
                                        onChange={(e) => setDearnessAllowance(e.target.value)}
                                        placeholder="e.g. 50000"
                                    />
                                </div>
                            </div>

                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="hraReceived">HRA Received (p.a.)*</label>
                                    <input
                                        type="number"
                                        id="hraReceived"
                                        value={hraReceived}
                                        onChange={(e) => setHraReceived(e.target.value)}
                                        placeholder="e.g. 200000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="totalRentPaid">Total Rent Paid (p.a.)*</label>
                                    <input
                                        type="number"
                                        id="totalRentPaid"
                                        value={totalRentPaid}
                                        onChange={(e) => setTotalRentPaid(e.target.value)}
                                        placeholder="e.g. 180000"
                                    />
                                </div>
                            </div>

                            <button onClick={calculateHRA} className="calculate-button">Calculate</button>
                        </div>

                        {hraTaxable !== null && (
                            <div className="calculator-result-section">
                                <h2>You Get</h2>
                                <div className="result-item">
                                    <span>HRA Taxable</span>
                                    <span className="result-value">₹{hraTaxable}</span>
                                </div>
                                <div className="result-item">
                                    <span>HRA Exempted</span>
                                    <span className="result-value">₹{hraExempted}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ChatbotAi />
        </div>
    );
};

export default HRACalculator;